import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest
  const [business, setBusiness] = useState(undefined);
  const [impersonating, setImpersonating] = useState(() => {
    try { return localStorage.getItem("impersonating_name") || null; } catch { return null; }
  });

  const loadMe = async () => {
    const token = localStorage.getItem("token");
    if (!token) { setUser(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data.role === "superadmin") setBusiness(undefined);
      else await loadBusiness();
    } catch {
      localStorage.removeItem("token");
      setUser(false);
    }
  };

  const loadBusiness = async () => {
    try {
      const { data } = await api.get("/business/me");
      setBusiness(data || null);
    } catch {
      setBusiness(null);
    }
  };

  useEffect(() => { loadMe(); }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.token);
    setUser(data.user);
    if (data.user.role === "superadmin") setBusiness(undefined);
    else await loadBusiness();
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    localStorage.setItem("token", data.token);
    setUser(data.user);
    setBusiness(null);
    return data.user;
  };

  const logout = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("admin_token");
      localStorage.removeItem("impersonating_name");
    } catch {}
    setImpersonating(null);
    setUser(false);
    setBusiness(undefined);
  };

  // Il superadmin entra nel gestionale di un'attività ("Entra come").
  const startImpersonation = async (bizId, bizName) => {
    const { data } = await api.post(`/admin/businesses/${bizId}/impersonate`);
    try {
      localStorage.setItem("admin_token", localStorage.getItem("token"));
      localStorage.setItem("token", data.token);
      localStorage.setItem("impersonating_name", bizName || data.business_name || "attività");
    } catch {}
    setImpersonating(bizName || data.business_name || "attività");
    setUser(data.user);
    await loadBusiness();
    return data.user;
  };

  // Torna all'amministrazione, ripristinando il token del superadmin.
  const stopImpersonation = async () => {
    try {
      const adminToken = localStorage.getItem("admin_token");
      if (adminToken) localStorage.setItem("token", adminToken);
      localStorage.removeItem("admin_token");
      localStorage.removeItem("impersonating_name");
    } catch {}
    setImpersonating(null);
    await loadMe();
  };

  return (
    <AuthContext.Provider value={{ user, business, setBusiness, login, register, logout,
      loadBusiness, impersonating, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}
